import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Permission } from '../types/user.types';
import { api } from '../config/api';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

// Cognito configuration
const poolData = {
  UserPoolId: 'us-west-2_L38R5O3fI',
  ClientId: '6eqrsln098junf5iu3bjvi7c20'
};
const userPool = new CognitoUserPool(poolData);

interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  permissions?: Permission[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token expiry check for JWT tokens
const isTokenExpired = (token: string): boolean => {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length === 3) {
      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      // JWT exp is in seconds, compare with current time in seconds
      return payload.exp < Math.floor(Date.now() / 1000);
    }
    // Fallback for demo tokens (stored as base64 JSON)
    const payload = JSON.parse(atob(token));
    return payload.exp < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate and restore session
  const validateSession = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('auth_token');

      if (storedUser && storedToken) {
        // Check if token is expired
        if (isTokenExpired(storedToken)) {
          console.warn('Auth token expired, clearing session');
          localStorage.removeItem('user');
          localStorage.removeItem('auth_token');
          setUser(null);
          return;
        }

        const parsedUser = JSON.parse(storedUser);

        // Validate user has required fields
        if (parsedUser.id && parsedUser.email && parsedUser.role) {
          // Restore auth header for API calls
          api.defaults.headers.common['Authorization'] = storedToken;
          setUser(parsedUser);
        } else {
          console.warn('Invalid user data in storage, clearing session');
          localStorage.removeItem('user');
          localStorage.removeItem('auth_token');
        }
      }
    } catch (error) {
      console.error('Auth validation failed:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await api.get('/auth/me');
      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        const updatedUser = {
          id: userData.id,
          email: userData.email,
          fullName: userData.fullName,
          role: userData.role,
          permissions: userData.permissions?.map((p: any) => p.permission) || []
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    // Validate input
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    // Normalize email
    const normalizedEmail = email.trim().toLowerCase();

    // Authenticate with Cognito
    return new Promise<void>((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: normalizedEmail,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: normalizedEmail,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (result) => {
          try {
            const idToken = result.getIdToken().getJwtToken();

            // Store token for API calls
            localStorage.setItem('auth_token', idToken);

            // Configure axios to use the token
            api.defaults.headers.common['Authorization'] = idToken;

            // Fetch current user details from our backend
            const meResponse = await api.get('/auth/me');

            if (meResponse.data.success && meResponse.data.data) {
              const userData = meResponse.data.data;
              const authenticatedUser: User = {
                id: userData.id,
                email: userData.email,
                fullName: userData.fullName,
                role: userData.role,
                permissions: userData.permissions?.map((p: any) => p.permission) || []
              };

              setUser(authenticatedUser);
              localStorage.setItem('user', JSON.stringify(authenticatedUser));
              resolve();
              return;
            }

            // User authenticated with Cognito but not found in backend database
            reject(new Error('User not found in system. Please contact administrator.'));
          } catch (error: any) {
            console.error('Error fetching user details:', error);
            reject(new Error('Failed to load user details'));
          }
        },
        onFailure: (err) => {
          console.error('Cognito auth failed:', err);
          if (err.code === 'NotAuthorizedException') {
            reject(new Error('Invalid email or password'));
          } else if (err.code === 'UserNotFoundException') {
            reject(new Error('User not found'));
          } else if (err.code === 'UserNotConfirmedException') {
            reject(new Error('Please verify your email before logging in'));
          } else {
            reject(new Error(err.message || 'Authentication failed'));
          }
        },
        newPasswordRequired: () => {
          reject(new Error('Password change required. Please contact administrator.'));
        }
      });
    });
  };

  const logout = useCallback(() => {
    // Sign out from Cognito
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');

    // Remove auth header
    delete api.defaults.headers.common['Authorization'];

    // Clear any other session-related data
    sessionStorage.clear();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
